import { Controller, useForm } from 'react-hook-form';
import { GENDERS } from '@/constants/professionalTags';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
	User,
	Phone,
	Mail,
	Video,
	GraduationCap,
	BriefcaseMedical,
	Building,
	Check,
	Save,
	Fingerprint,
	Globe,
	X,
} from 'lucide-react';
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { maskCNPJ, maskCPF, maskPhone, removeMask } from '@/utils/masks';
import { SERVICE_MODALITY_TRANSLATIONS } from '@/constants/professional/translations';
import { Button } from '@/components/ui/Button';
import { useEffect, useState } from 'react';
import { Megaphone, TriangleAlert } from 'lucide-react';
import {
	ProfessionalPublicTagsFields,
	isSamePublicTags,
	toPublicTagsValue,
	type ProfessionalPublicTagsValue,
} from '@/components/professional/forms/ProfessionalPublicTagsFields';
import { isPsychologyServiceName } from '@/constants/professionalTags';

// --- SCHEMAS ---
const personalSchema = z.object({
	first_name: z
		.string()
		.min(1, 'Nome é obrigatório')
		.min(3, 'Mínimo 3 caracteres')
		.max(255)
		.regex(/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/),
	last_name: z
		.string()
		.min(1, 'Sobrenome é obrigatório')
		.min(3, 'Mínimo 3 caracteres')
		.max(255)
		.regex(/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/),
	phone_number: z
		.string()
		.min(1, 'Obrigatório')
		.transform(removeMask)
		.refine((val) => val.length === 11, 'Deve conter 11 números')
		.optional()
		.or(z.literal('')),
	crm_or_crp: z
		.string()
		.min(1, 'CRM/CRP é obrigatório')
		.regex(/^[A-Za-z0-9\/-]+$/, 'Formato inválido'),
	cpf_or_cnpj: z
		.string()
		.min(1, 'CPF/CNPJ é obrigatório')
		.transform((s) => removeMask(s).slice(0, 14))
		.refine(
			(val) => val.length === 11 || val.length === 14,
			'Documento inválido',
		),
	email: z.string().email(),
	name_prefix: z.string().nullable().optional().or(z.literal('')),
	profile_photo_url: z.string().optional(),
	gender: z
		.enum(['FEMININO', 'MASCULINO', 'PREFIRO_NAO_INFORMAR'])
		.optional(),
});

const professionalSchema = z.object({
	clinic_id: z
		.string()
		.uuid('Clínica selecionada inválida')
		.optional()
		.or(z.literal(''))
		.or(z.null()),
	service_modality: z.enum(['HYBRID', 'FULL_REMOTE', 'FULL_PRESENCIAL']),
	rqe: z.string().optional().or(z.literal('')),
	lattes_url: z
		.string()
		.min(1, 'Obrigatória')
		.url('URL inválida')
		.optional()
		.or(z.literal('')),
	video_url: z.string().url('URL inválida').optional().or(z.literal('')),
	brief_description: z
		.string()
		.max(255, 'Máximo 255 caracteres')
		.optional()
		.or(z.literal('')),
});

// Inferência de Tipos para uso local no formulário
type PersonalFormValues = z.infer<typeof personalSchema>;
type ProfessionalFormValues = z.infer<typeof professionalSchema>;

// --- ESTILOS COMPARTILHADOS ---
const labelStyle =
	'flex items-center gap-2 text-[9px] font-black text-brand-purple uppercase tracking-widest ml-1 leading-none mb-1.5';
const inputStyle =
	'w-full bg-stone-50/50 border border-stone-100 rounded-xl px-4 py-2.5 text-stone-700 text-sm font-bold outline-none focus:bg-white focus:border-stone-300 transition-all placeholder:text-stone-300 placeholder:font-normal';
const errorStyle = 'text-red-500 text-[10px] font-bold ml-1 mt-1';
const cardStyle =
	'bg-white border border-stone-100 rounded-[2rem] p-6 md:p-8 shadow-sm space-y-6';

const readonlyInputStyle = `${inputStyle} cursor-not-allowed border-dashed border-stone-200 bg-stone-50/30 text-stone-400`;

const PERSONAL_LOCKED_KEYS = new Set<keyof PersonalFormValues>([
	'email',
	'cpf_or_cnpj',
]);

const formatCpfCnpjDisplay = (value: string | undefined): string => {
	const d = removeMask(value ?? '').slice(0, 14);
	if (!d) return '';
	return d.length <= 11 ? maskCPF(d) : maskCNPJ(d);
};

// --- FORMULÁRIO PESSOAL ---
const PersonalInfoForm = ({
	defaultValues,
	isSubmitting,
	onSubmitPersonalData,
}: {
	defaultValues?: Professionals.Professional;
	isSubmitting: boolean;
	onSubmitPersonalData: (
		data: Partial<PersonalFormValues>,
	) => void | Promise<void>;
}) => {
	const {
		register,
		handleSubmit,
		formState: { errors, dirtyFields },
		setValue,
		reset,
	} = useForm<PersonalFormValues>({
		resolver: zodResolver(personalSchema),
		defaultValues: {
			first_name: defaultValues?.first_name ?? '',
			last_name: defaultValues?.last_name ?? '',
			phone_number: defaultValues?.phone_number ?? '',
			crm_or_crp: defaultValues?.crm_or_crp ?? '',
			cpf_or_cnpj: formatCpfCnpjDisplay(defaultValues?.cpf_or_cnpj),
			email: defaultValues?.email ?? '',
			name_prefix: (defaultValues as any)?.name_prefix ?? '',
			profile_photo_url: defaultValues?.profile_photo_url ?? undefined,
			gender: defaultValues?.gender ?? 'PREFIRO_NAO_INFORMAR',
		},
	});

	useEffect(() => {
		if (!defaultValues) return;
		reset({
			first_name: defaultValues.first_name ?? '',
			last_name: defaultValues.last_name ?? '',
			phone_number: defaultValues.phone_number
				? maskPhone(defaultValues.phone_number)
				: '',
			crm_or_crp: defaultValues.crm_or_crp ?? '',
			cpf_or_cnpj: formatCpfCnpjDisplay(defaultValues.cpf_or_cnpj),
			email: defaultValues.email ?? '',
			name_prefix: (defaultValues as any)?.name_prefix ?? '',
			profile_photo_url: defaultValues.profile_photo_url ?? undefined,
			gender: defaultValues.gender ?? 'PREFIRO_NAO_INFORMAR',
		});
	}, [
		defaultValues?.id,
		defaultValues?.first_name,
		defaultValues?.last_name,
		defaultValues?.phone_number,
		defaultValues?.crm_or_crp,
		defaultValues?.cpf_or_cnpj,
		defaultValues?.email,
		(defaultValues as any)?.name_prefix,
		defaultValues?.profile_photo_url,
	]);

	return (
		<div className={cardStyle}>
			<div className="mb-2 flex items-center gap-3">
				<div className="rounded-lg bg-brand-purple/10 p-2 text-brand-purple">
					<Fingerprint size={20} />
				</div>
				<h3 className="text-lg font-black tracking-tight text-stone-900">
					Informações Pessoais
				</h3>
			</div>

			<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
				<div className="text-left">
					<label className={labelStyle}>
						<User size={10} /> Nome
					</label>
					<input
						{...register('first_name')}
						className={inputStyle}
						placeholder="Seu nome"
					/>
					{errors.first_name && (
						<p className={errorStyle}>
							{errors.first_name.message}
						</p>
					)}
				</div>
				<div className="text-left">
					<label className={labelStyle}>
						<User size={10} /> Sobrenome
					</label>
					<input
						{...register('last_name')}
						className={inputStyle}
						placeholder="Seu sobrenome"
					/>
					{errors.last_name && (
						<p className={errorStyle}>{errors.last_name.message}</p>
					)}
				</div>
			</div>

			<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
				<div className="text-left">
					<label className={labelStyle}>
						<Fingerprint size={10} /> CRM/CRP
					</label>
					<input
						{...register('crm_or_crp')}
						className={inputStyle}
						placeholder="Seu CRM/CRP"
					/>
					{errors.crm_or_crp && (
						<p className={errorStyle}>
							{errors.crm_or_crp.message}
						</p>
					)}
				</div>

				<div className="text-left">
					<label className={labelStyle}>
						<Fingerprint size={10} /> CPF/CNPJ
					</label>
					<input
						{...register('cpf_or_cnpj')}
						readOnly
						tabIndex={-1}
						className={readonlyInputStyle}
						placeholder="—"
					/>
					{errors.cpf_or_cnpj && (
						<p className={errorStyle}>
							{errors.cpf_or_cnpj.message}
						</p>
					)}
				</div>
			</div>

			<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
				<div className="text-left">
					<label className={labelStyle}>
						<User size={10} /> Prefixo
					</label>
					<input
						{...register('name_prefix')}
						className={inputStyle}
						placeholder="Dr., Dra., etc."
					/>
				</div>
				<div className="text-left">
					<label className={labelStyle}>
						<User size={10} /> Gênero (opcional)
					</label>
					<select {...register('gender')} className={inputStyle}>
						{GENDERS.map((g) => (
							<option key={g.key} value={g.key}>
								{g.label}
							</option>
						))}
					</select>
					<p className="ml-1 mt-1 text-[10px] text-stone-400">
						Só entra no match quando informado; quem prefere não
						informar não é penalizado.
					</p>
				</div>
			</div>

			<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
				<div className="text-left">
					<label className={labelStyle}>
						<Mail size={10} /> E-mail
					</label>
					<input
						{...register('email')}
						readOnly
						tabIndex={-1}
						className={readonlyInputStyle}
					/>
				</div>

				<div className="text-left">
					<label className={labelStyle}>
						<Phone size={10} /> Telefone (opcional)
					</label>
					<input
						{...register('phone_number')}
						onChange={(e) =>
							setValue(
								'phone_number',
								maskPhone(e.target.value),
								{
									shouldDirty: true,
								},
							)
						}
						className={inputStyle}
						placeholder="(00) 00000-0000"
					/>
					{errors.phone_number && (
						<p className={errorStyle}>
							{errors.phone_number.message}
						</p>
					)}
				</div>
			</div>

			{Object.keys(dirtyFields).length > 0 && (
				<div className="animate-in fade-in slide-in-from-bottom-2 flex justify-end pt-2">
					<Button
						onClick={handleSubmit(async (data) => {
							const dirtyKeys = Object.keys(dirtyFields) as Array<
								keyof PersonalFormValues
							>;
							const entries = dirtyKeys
								.filter(
									(k) =>
										dirtyFields[k] &&
										!PERSONAL_LOCKED_KEYS.has(k),
								)
								.map((k) => [k, data[k]] as [string, unknown]);
							const patch = Object.fromEntries(
								entries,
							) as Partial<PersonalFormValues>;
							await onSubmitPersonalData(patch);
							reset({
								first_name: data.first_name,
								last_name: data.last_name,
								phone_number: data.phone_number
									? maskPhone(data.phone_number)
									: '',
								crm_or_crp: data.crm_or_crp,
								cpf_or_cnpj: formatCpfCnpjDisplay(
									String(data.cpf_or_cnpj ?? ''),
								),
								email: data.email,
								name_prefix: data.name_prefix,
								profile_photo_url: data.profile_photo_url,
							});
						})}
						disabled={isSubmitting}
						text="Salvar Dados Pessoais"
						className="h-auto rounded-xl border-none bg-black px-8 py-3 text-xs font-bold uppercase tracking-widest text-white shadow-lg shadow-stone-100 transition-all hover:bg-stone-800 active:scale-[0.98]"
						endIcon={<Save size={16} className="ml-1" />}
					/>
				</div>
			)}
		</div>
	);
};

// --- FORMULÁRIO PROFISSIONAL ---
const ProfessionalInfoForm = ({
	defaultValues,
	onSubmitProfessionalData,
	isSubmitting,
	clinics,
}: {
	defaultValues?: Professionals.Professional;
	onSubmitProfessionalData: (
		data: Partial<ProfessionalFormValues>,
	) => void | Promise<void>;
	isSubmitting: boolean;
	clinics: Array<Clinics.Clinic>;
}) => {
	const {
		register,
		handleSubmit,
		formState: { errors, dirtyFields },
		control,
		reset,
	} = useForm<ProfessionalFormValues>({
		resolver: zodResolver(professionalSchema),
		defaultValues: {
			clinic_id: (defaultValues?.clinic_id as any) ?? null,
			service_modality:
				(defaultValues?.service_modality as any) ?? undefined,
			rqe: defaultValues?.rqe ?? '',
			lattes_url: defaultValues?.lattes_url ?? '',
			video_url: defaultValues?.video_url ?? '',
			brief_description: defaultValues?.brief_description ?? '',
		},
	});

	useEffect(() => {
		if (!defaultValues) return;
		reset({
			clinic_id: (defaultValues.clinic_id as any) ?? null,
			service_modality:
				(defaultValues.service_modality as any) ?? undefined,
			rqe: defaultValues.rqe ?? '',
			lattes_url: defaultValues.lattes_url ?? '',
			video_url: defaultValues.video_url ?? '',
			brief_description: defaultValues.brief_description ?? '',
		});
	}, [
		defaultValues?.id,
		defaultValues?.clinic_id,
		defaultValues?.service_modality,
		defaultValues?.rqe,
		defaultValues?.lattes_url,
		defaultValues?.video_url,
		defaultValues?.brief_description,
	]);

	const findClinicName = (id: string | undefined) =>
		clinics.find((c) => c.id === id)?.name;

	return (
		<div className={cardStyle}>
			<div className="mb-2 flex items-center gap-3">
				<div className="rounded-lg bg-brand-purple/10 p-2 text-brand-purple">
					<BriefcaseMedical size={20} />
				</div>
				<h3 className="text-lg font-black tracking-tight text-stone-900">
					Informações Profissionais
				</h3>
			</div>

			{(defaultValues?.professional_services?.[0]?.service
				?.service_name ??
				defaultValues?.professional_services?.[0]?.service
					?.professional_label) && (
				<div className="space-y-1.5 text-left">
					<label className={labelStyle}>
						<BriefcaseMedical size={10} /> Serviço prestado
					</label>
					<p className={`${inputStyle} bg-stone-50/80 font-semibold`}>
						{defaultValues.professional_services?.[0]?.service
							?.service_name ??
							defaultValues.professional_services?.[0]?.service
								?.professional_label}
					</p>
				</div>
			)}

			<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
				<div className="space-y-1.5 text-left">
					<label className={labelStyle}>
						<Building size={10} /> Clínica
					</label>
					<Controller
						name="clinic_id"
						control={control}
						render={({ field }) => (
							<Select
								value={field.value ?? ''}
								onValueChange={(val) =>
									field.onChange(val || null)
								}
							>
								<SelectTrigger
									className={`${inputStyle} h-auto`}
								>
									<SelectValue>
										{findClinicName(
											field.value as string | undefined,
										) ||
											(defaultValues?.clinic_id === null
												? 'Online'
												: 'Escolha a clínica')}
									</SelectValue>
								</SelectTrigger>
								<SelectContent>
									<SelectGroup>
										{clinics.map((clinic) => (
											<SelectItem
												key={clinic.id}
												value={clinic.id}
											>
												{clinic.name}
											</SelectItem>
										))}
									</SelectGroup>
								</SelectContent>
							</Select>
						)}
					/>
					{errors.clinic_id && (
						<p className={errorStyle}>{errors.clinic_id.message}</p>
					)}
				</div>

				<div className="space-y-1.5 text-left">
					<label className={labelStyle}>
						<BriefcaseMedical size={10} /> Modalidade
					</label>
					<Controller
						name="service_modality"
						control={control}
						render={({ field }) => (
							<Select
								value={field.value}
								onValueChange={field.onChange}
							>
								<SelectTrigger
									className={`${inputStyle} h-auto`}
								>
									<SelectValue>
										{field.value
											? SERVICE_MODALITY_TRANSLATIONS[
													field.value as Professionals.ServiceModality
											  ]
											: 'Escolher'}
									</SelectValue>
								</SelectTrigger>
								<SelectContent>
									<SelectGroup>
										{[
											'FULL_PRESENCIAL',
											'FULL_REMOTE',
											'HYBRID',
										].map((modality) => (
											<SelectItem
												key={modality}
												value={modality}
											>
												{
													SERVICE_MODALITY_TRANSLATIONS[
														modality as Professionals.ServiceModality
													]
												}
											</SelectItem>
										))}
									</SelectGroup>
								</SelectContent>
							</Select>
						)}
					/>
					{errors.service_modality && (
						<p className={errorStyle}>
							{errors.service_modality.message}
						</p>
					)}
				</div>
			</div>

			<div className="text-left">
				<label className={labelStyle}>
					<Fingerprint size={10} /> RQE (Opcional)
				</label>
				<input
					{...register('rqe')}
					className={inputStyle}
					placeholder="Seu RQE"
				/>
				{errors.rqe && (
					<p className={errorStyle}>{errors.rqe.message}</p>
				)}
			</div>

			<div className="text-left">
				<label className={labelStyle}>
					<Video size={10} /> URL de Vídeo (Opcional)
				</label>
				<input
					{...register('video_url')}
					className={inputStyle}
					placeholder="https://youtube.com..."
				/>
				{errors.video_url && (
					<p className={errorStyle}>{errors.video_url.message}</p>
				)}
			</div>

			<div className="text-left">
				<label className={labelStyle}>
					<Globe size={10} /> URL Lattes/Cúrriculo (Opcional)
				</label>
				<input
					{...register('lattes_url')}
					className={inputStyle}
					placeholder="Link do seu currículo Lattes"
				/>
				{errors.lattes_url && (
					<p className={errorStyle}>{errors.lattes_url.message}</p>
				)}
			</div>

			<div className="text-left">
				<label className={labelStyle}>
					<GraduationCap size={10} /> Descrição Breve (opcional)
				</label>
				<textarea
					{...register('brief_description')}
					className={`${inputStyle} min-h-[100px] resize-none`}
					placeholder="Especialista em..."
				/>
				{errors.brief_description && (
					<p className={errorStyle}>
						{errors.brief_description.message}
					</p>
				)}
			</div>

			{Object.keys(dirtyFields).length > 0 && (
				<div className="animate-in fade-in slide-in-from-bottom-2 flex justify-end pt-2">
					<Button
						onClick={handleSubmit(async (data) => {
							const dirtyKeys = Object.keys(dirtyFields) as Array<
								keyof ProfessionalFormValues
							>;
							const entries = dirtyKeys
								.filter((k) => dirtyFields[k])
								.map((k) => [k, data[k]] as [string, unknown]);
							const patch = Object.fromEntries(
								entries,
							) as Partial<ProfessionalFormValues>;
							if ('clinic_id' in patch && !patch.clinic_id) {
								delete patch.clinic_id;
							}
							await onSubmitProfessionalData(patch);
							reset(data);
						})}
						disabled={isSubmitting}
						text="Salvar Dados Profissionais"
						className="h-auto rounded-xl border-none bg-black px-8 py-3 text-xs font-bold uppercase tracking-widest text-white shadow-lg shadow-stone-100 transition-all hover:bg-stone-800 active:scale-[0.98]"
						endIcon={<Save size={16} className="ml-1" />}
					/>
				</div>
			)}
		</div>
	);
};

// --- EXIBIÇÃO PÚBLICA (TAGS DO CARD) ---
const PublicDisplayForm = ({
	defaultValues,
	onSubmitPublicDisplay,
	isSubmitting,
}: {
	defaultValues?: Professionals.Professional;
	onSubmitPublicDisplay: (
		data: ProfessionalPublicTagsValue,
	) => void | Promise<void>;
	isSubmitting: boolean;
}) => {
	// `baseline` é o último valor salvo (a query do perfil não é revalidada após o PATCH).
	const [baseline, setBaseline] = useState<ProfessionalPublicTagsValue>(() =>
		toPublicTagsValue(defaultValues),
	);
	const [value, setValue] = useState<ProfessionalPublicTagsValue>(baseline);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		const next = toPublicTagsValue(defaultValues);
		setBaseline(next);
		setValue(next);
	}, [
		defaultValues?.id,
		defaultValues?.highlight_tag,
		defaultValues?.main_approach,
		defaultValues?.secondary_approaches?.join(),
		defaultValues?.focus_topics?.join(),
	]);

	const service = defaultValues?.professional_services?.[0]?.service;
	const isPsychology = isPsychologyServiceName(
		service?.service_name,
		service?.professional_label,
	);
	const missingMainApproach = isPsychology && !value.main_approach;
	const changed = !isSamePublicTags(value, baseline);

	const handleSave = async () => {
		setSaving(true);
		try {
			const payload: ProfessionalPublicTagsValue = {
				highlight_tag: value.highlight_tag ?? null,
				main_approach: value.main_approach ?? null,
				secondary_approaches: [...value.secondary_approaches],
				focus_topics: [...value.focus_topics],
			};
			await onSubmitPublicDisplay(payload);
			setBaseline(payload);
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className={cardStyle}>
			<div className="mb-2 flex items-center gap-3">
				<div className="rounded-lg bg-brand-purple/10 p-2 text-brand-purple">
					<Megaphone size={20} />
				</div>
				<div className="text-left">
					<h3 className="text-lg font-black tracking-tight text-stone-900">
						Exibição pública
					</h3>
					<p className="text-xs text-stone-400">
						Como você aparece nos cards de busca e agendamento.
					</p>
				</div>
			</div>

			{missingMainApproach && (
				<div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-left text-xs font-semibold text-amber-800">
					<TriangleAlert size={16} className="mt-0.5 shrink-0" />
					<span>
						Informe sua abordagem principal para aparecer melhor nas
						buscas.
					</span>
				</div>
			)}

			<ProfessionalPublicTagsFields
				value={value}
				onChange={setValue}
				requireMainApproach={isPsychology}
				disabled={isSubmitting || saving}
			/>

			{changed && (
				<div className="animate-in fade-in slide-in-from-bottom-2 flex justify-end pt-2">
					<Button
						onClick={handleSave}
						disabled={isSubmitting || saving}
						text={saving ? 'Salvando...' : 'Salvar exibição'}
						className="h-auto rounded-xl border-none bg-black px-8 py-3 text-xs font-bold uppercase tracking-widest text-white shadow-lg shadow-stone-100 transition-all hover:bg-stone-800 active:scale-[0.98]"
						endIcon={<Save size={16} className="ml-1" />}
					/>
				</div>
			)}
		</div>
	);
};

// --- COMPONENTE PRINCIPAL ---
const ProfessionalProfileForm = ({
	onSubmit,
	defaultValues,
	isSubmitting = false,
	onBack: _onBack,
	clinics,
}: {
	onSubmit: (
		data: Partial<Professionals.Professional>,
	) => void | Promise<void>;
	onBack: () => void;
	defaultValues?: Professionals.Professional;
	isSubmitting?: boolean;
	clinics: Array<any>;
}) => {
	const professionalDisplayName = [
		(defaultValues as any)?.name_prefix,
		defaultValues?.first_name,
		defaultValues?.last_name,
	]
		.filter(Boolean)
		.join(' ');
	const initials = `${defaultValues?.first_name?.charAt(0) || ''}${
		defaultValues?.last_name?.charAt(0) || ''
	}`.toUpperCase();

	const hasGooglePermissions = !!defaultValues?.google_permissions_allowed;
	const hasVerifiedEmail = !!defaultValues?.verified_email;

	return (
		<div className="space-y-6">
			{/* CARD DE IDENTIDADE (VISUAL APENAS) */}
			<div className="flex items-center gap-6 rounded-[2rem] border border-stone-100 bg-white p-6 shadow-sm">
				<div className="relative shrink-0">
					{defaultValues?.profile_photo_url ? (
						<img
							src={defaultValues.profile_photo_url}
							alt={`${defaultValues.first_name} ${defaultValues.last_name}`}
							className="h-20 w-20 rounded-3xl object-cover shadow-xl shadow-brand-purple/25"
							referrerPolicy="no-referrer"
						/>
					) : (
						<div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-brand-purple text-2xl font-black text-white shadow-xl shadow-brand-purple/25">
							{initials}
						</div>
					)}
				</div>

				<div className="space-y-1.5 text-left">
					<div className="flex items-center gap-2">
						<h2 className="text-xl font-black text-stone-900">
							{professionalDisplayName}
						</h2>
						<div
							className={`h-2.5 w-2.5 rounded-full border-2 border-white shadow-sm ${
								hasVerifiedEmail
									? 'bg-brand-purple'
									: 'bg-stone-300'
							}`}
						/>
					</div>
					<div className="flex flex-wrap gap-2 pt-1">
						<span
							className={`flex items-center gap-1 rounded-md border px-2 py-1 text-[8px] font-black uppercase tracking-widest ${
								hasGooglePermissions
									? 'border-emerald-100 bg-emerald-50 text-emerald-600'
									: 'border-stone-200 bg-stone-50 text-stone-500'
							}`}
						>
							{hasGooglePermissions ? (
								<Check size={8} strokeWidth={4} />
							) : (
								<X
									size={8}
									strokeWidth={4}
									className="opacity-60"
								/>
							)}
							{hasGooglePermissions
								? 'Permissões concedidas no Google'
								: 'Permissões não concedidas no Google'}
						</span>

						<span
							className={`flex items-center gap-1 rounded-md border px-2 py-1 text-[8px] font-black uppercase tracking-widest ${
								hasVerifiedEmail
									? 'border-emerald-100 bg-emerald-50 text-emerald-600'
									: 'border-stone-200 bg-stone-50 text-stone-500'
							}`}
						>
							{hasVerifiedEmail ? (
								<Check size={8} strokeWidth={4} />
							) : (
								<X
									size={8}
									strokeWidth={4}
									className="opacity-60"
								/>
							)}
							{hasVerifiedEmail
								? 'Verificado pelo Google'
								: 'Não verificado pelo Google'}
						</span>
					</div>
				</div>
			</div>

			<PersonalInfoForm
				defaultValues={defaultValues}
				onSubmitPersonalData={(data) => onSubmit(data as any)}
				isSubmitting={isSubmitting}
			/>

			<ProfessionalInfoForm
				defaultValues={defaultValues}
				onSubmitProfessionalData={async (data) => {
					const payload: any = { ...data };
					if (!payload.clinic_id) {
						delete payload.clinic_id;
					}
					await onSubmit(payload);
				}}
				isSubmitting={isSubmitting}
				clinics={clinics}
			/>

			<PublicDisplayForm
				defaultValues={defaultValues}
				onSubmitPublicDisplay={(data) => onSubmit(data)}
				isSubmitting={isSubmitting}
			/>
		</div>
	);
};

export { ProfessionalProfileForm };
